import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { listRestaurants } from '../services/api';
import ActionMenu from '../components/ActionMenu';
import Icon from '../components/Icon';
import { storage } from '../utils/storage';
import './RestaurantPage.css';

function RestaurantPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  // Toast de éxito tras crear/editar un restaurante (NER-4031). Llega vía
  // navigate(..., { state: { message } }) desde RestaurantFormPage.
  const [successMessage, setSuccessMessage] = useState(location.state?.message || '');

  useEffect(() => {
    loadRestaurants();
  }, []);

  useEffect(() => {
    if (!location.state?.message) return;
    // Limpiar el state del history para que no reaparezca al refrescar/volver.
    window.history.replaceState({}, document.title);
    const timer = setTimeout(() => setSuccessMessage(''), 4000);
    return () => clearTimeout(timer);
  }, [location.state]);

  const loadRestaurants = async () => {
    try {
      setLoading(true);
      const data = await listRestaurants();
      setRestaurants(data.restaurants || []);
    } catch (error) {
      console.error('Error loading restaurants:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRestaurant = () => {
    navigate('/restaurant/new');
  };

  const handleEditRestaurant = (restaurantId) => {
    navigate(`/restaurant/edit/${restaurantId}`);
  };

  // Restaurante activo del panel. Mismo mecanismo que el selector del menú de
  // usuario (QA 2026-09-13: no se encontraba cómo cambiar de restaurante, lo que
  // bloqueó probar las plantillas de organización entre restaurantes).
  const activeRestaurantId = storage.get('selectedRestaurantId');
  const handleUseRestaurant = (restaurant) => {
    storage.set('selectedRestaurantId', restaurant.restaurantId);
    storage.set('selectedRestaurantName', restaurant.name);
    window.location.reload();
  };

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <div className="restaurant-page">
      {successMessage && <div className="message success">{successMessage}</div>}
      <div className="page-header">
        <div>
          <h1>{t('restaurants.title')}</h1>
          <p>{t('restaurants.subtitle')}</p>
          {restaurants.length > 1 && (
            <p className="restaurant-switch-hint">{t('restaurants.switchHint')}</p>
          )}
        </div>
        <button type="button" className="btn btn-primary" onClick={handleCreateRestaurant}>
          + {t('restaurants.newRestaurant')}
        </button>
      </div>

      {restaurants.length === 0 ? (
        <div className="empty-state">
          <h2>{t('restaurants.noRestaurants')}</h2>
          <p>{t('restaurants.createFirstRestaurant')}</p>
          <button type="button" className="btn btn-primary" onClick={handleCreateRestaurant}>
            {t('restaurants.createRestaurant')}
          </button>
        </div>
      ) : (
        <div className="restaurants-grid">
          {restaurants.map((restaurant) => (
            <div key={restaurant.restaurantId} className="restaurant-card">
              <div className="restaurant-header">
                <h2>{restaurant.name}</h2>
                <div className="restaurant-header-right">
                  {restaurant.restaurantId === activeRestaurantId && (
                    <span className="status-badge restaurant-active-badge">
                      {t('restaurants.activeRestaurant')}
                    </span>
                  )}
                  <span className={`status-badge ${restaurant.status}`}>
                    {restaurant.status === 'active' ? t('common.active') : t('common.inactive')}
                  </span>
                  <ActionMenu
                    actions={[
                      ...(restaurant.restaurantId !== activeRestaurantId
                        ? [
                            {
                              label: t('restaurants.useThisRestaurant'),
                              icon: 'check_circle',
                              onClick: () => handleUseRestaurant(restaurant),
                              variant: 'default',
                            },
                          ]
                        : []),
                      {
                        label: t('restaurants.editRestaurant'),
                        icon: 'edit',
                        onClick: () => handleEditRestaurant(restaurant.restaurantId),
                        variant: 'default',
                      },
                    ]}
                  />
                </div>
              </div>
              <div className="restaurant-details">
                <p>
                  <Icon name="place" size="small" className="detail-icon" /> {restaurant.address}
                </p>
                {restaurant.phone && (
                  <p>
                    <Icon name="phone" size="small" className="detail-icon" /> {restaurant.phone}
                  </p>
                )}
                {restaurant.email && (
                  <p>
                    <Icon name="email" size="small" className="detail-icon" /> {restaurant.email}
                  </p>
                )}
              </div>
              {restaurant.restaurantId !== activeRestaurantId && (
                <div className="restaurant-card-footer">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleUseRestaurant(restaurant)}
                  >
                    {t('restaurants.useThisRestaurant')}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default RestaurantPage;
